// FIXME: remove eventually
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { customerSelector } from '@/api/v1';
import { StepperProgressWidget } from '@/components/organisms/StepperProgressWidget';
import { StepperStepsOverview } from '@/components/organisms/StepperStepsOverview';
import { VerifyBusinessHeader } from '@/components/organisms/VerifyBusinessHeader';
import { VerifyBusinessModal } from '@/components/organisms/VerifyBusinessModal';
import { VerifyBusinessProvider } from '@/components/organisms/VerifyBusinessProvider';
import { useStepperFormName } from '@/hooks/useStepperFormName';
import { useTranslator } from '@/hooks/useTranslator';
import { useVerifyBusinessSteps } from '@/hooks/useVerifyBusinessSteps';
import { getCompletedStepsCount, getTotalStepsCount } from '@/Screens/VerifyBusiness/helpers';
import {
  initialState,
  VerifyBusinessAction,
  verifyBusinessReducer,
  VerifyBusinessState,
} from '@/Screens/VerifyBusiness/reducer';
import { computeNextStepPath } from '@/utility/computeNextStepPath';
import { computeValidRoutePath } from '@/utility/computeValidRoutePath';
import { AsAnyActionArg } from '@/utility/reducers';
import { runWhenReady } from '@/utility/runWhenReady';

type StepParameters = {
  readonly parentId: string;
  readonly childId: string;
};

export const VerifyBusinessLayout: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const steps = useVerifyBusinessSteps();
  const reducer = React.useReducer<VerifyBusinessState, AsAnyActionArg<VerifyBusinessAction>>(
    verifyBusinessReducer,
    initialState,
  );

  const [visited, setVisited] = React.useState<boolean>(false);
  const [state] = reducer;

  const { data: customer } = useSelector(customerSelector);
  const { parentId, childId } = useParams<StepParameters>();
  const { statuses } = state;

  const tr = useTranslator();
  const location = useLocation();

  const markAsVisited = React.useCallback((): void => {
    setVisited(true);
  }, []);

  const checkModalVisibility = React.useCallback((): void => {
    const state = location.state;
    if (!state) {
      return;
    }

    if (state.edit) {
      setVisited(true);
    }
  }, [location.state]);

  const validateRouterPath = React.useCallback((): void => {
    const validRoutePath = computeValidRoutePath(steps, parentId, childId);
    if (validRoutePath) {
      navigate(validRoutePath);
    }
  }, [childId, navigate, parentId, steps]);

  React.useLayoutEffect((): void => {
    checkModalVisibility();
    validateRouterPath();
  }, [checkModalVisibility, validateRouterPath]);

  const nextStep = React.useMemo((): string | null => {
    return computeNextStepPath(steps, parentId, childId);
  }, [childId, parentId, steps]);

  const goToNext = React.useCallback((): void => {
    if (nextStep !== null) {
      navigate(['..', nextStep].join('/'));
    }
  }, [navigate, nextStep]);

  const formName = useStepperFormName();

  const totalStepsCount = React.useMemo(
    (): number => getTotalStepsCount(steps, customer),
    [customer, steps],
  );

  const completedStepsCount = React.useMemo(
    (): number => getCompletedStepsCount(steps, statuses, customer),
    [customer, statuses, steps],
  );

  const onNext = React.useCallback((): void => {
    // This is just so that we ensure the onSubmit handler is called first
    runWhenReady(goToNext);
  }, [goToNext]);

  // FIXME: maybe there's a simpler way to get if this is the last step?
  const isLastStep = React.useMemo((): boolean => {
    if (steps.length === 0) {
      console.debug('No steps found in VerifyBusinessLayout');
      return false;
    }

    const { children } = steps[steps.length - 1];
    if (children.length === 0) {
      return false;
    }

    return children[children.length - 1].type === childId;
  }, [childId, steps]);

  const onSubmit = React.useCallback((): void => {
    navigate('/verify-business/summary', { replace: true });
  }, [navigate]);

  const NextButton = React.useMemo(
    (): React.FC =>
      function NextButton(): React.ReactElement {
        if (isLastStep) {
          const disabled = completedStepsCount < totalStepsCount;
          return (
            <Button
              sx={styles.nextButton}
              variant="contained"
              form={formName}
              disabled={disabled}
              onClick={onSubmit}>
              {tr('Review and submit')}
            </Button>
          );
        }

        return (
          <Button sx={styles.nextButton} variant="contained" form={formName} onClick={onNext}>
            {tr('Next')}
          </Button>
        );
      },
    [completedStepsCount, formName, isLastStep, onNext, onSubmit, totalStepsCount, tr],
  );

  if (!parentId || !childId) {
    return <></>;
  }

  return (
    <VerifyBusinessProvider reducer={reducer}>
      <Box sx={styles.container}>
        <VerifyBusinessHeader formName={formName} />

        <Box sx={styles.leftPanel}>
          <StepperStepsOverview steps={steps} statuses={statuses} />
          <StepperProgressWidget
            currentCount={completedStepsCount}
            totalCount={totalStepsCount}
            onSubmit={onSubmit}
          />
        </Box>

        <Box sx={styles.stepContainer}>
          <Paper elevation={0}>
            <Outlet key={formName} />
          </Paper>

          <Box display="flex" alignItems="center" justifyContent="flex-end" my={4}>
            <NextButton />
          </Box>
        </Box>
      </Box>

      <VerifyBusinessModal
        open={!visited}
        statuses={statuses}
        steps={steps}
        onClose={markAsVisited}
      />
    </VerifyBusinessProvider>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
    display: 'grid',
    gridTemplateRows: '104px 1fr',
    gridTemplateColumns: '1fr 317px 771px 1fr',
    rowGap: 7,
    columnGap: 5,
    height: '100%',
    width: '100%',
    overflowY: 'scroll',
    backgroundColor: 'custom.InputsBackground',
  },
  contentContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    gap: 5,
    margin: 'auto',
    flexGrow: 1,
    py: 7,
  },
  stepContainer: {
    flexShrink: 0,
    flexGrow: 0,
    height: '100%',
    width: 771,
  },
  leftPanel: {
    display: 'flex',
    position: 'sticky',
    gridColumn: '2',
    top: 160,
    alignSelf: 'start',
    flexDirection: 'column',
    flexShrink: 0,
    flexGrow: 0,
    gap: 3,
    width: 317,
  },
  nextButton: {
    minWidth: 167,
  },
};
